import {fetchCurrentSubscription, Subscription} from './SubscriptionService';

/**
 * Модульный кеш эффективной подписки (`GET /api/subscription/current`) по образцу
 * `refsCache.ts` / `preferencesCache.ts` (CTHS-110).
 *
 * Тариф читают сразу несколько поверхностей: премиум-замки в меню (`App.tsx`, R17),
 * бейдж в шапке (`UserAvatar`) и карточка в профиле. Без общего кеша каждое
 * монтирование било бы в эндпоинт заново, а открытие меню добавляло бы ещё один
 * запрос поверх уже сделанного приложением.
 *
 * TTL короткий: тариф меняется редко, но если он всё-таки сменился (апгрейд на
 * внешнем портале, действие администратора), пользователь не должен ждать
 * перезагрузки вкладки. Явная инвалидация — {@link invalidateSubscriptionCache}.
 *
 * Кеш живёт только в памяти вкладки: в `localStorage` подписка не попадает и при
 * выходе из аккаунта сбрасывается, поэтому чужой тариф не переживает смену
 * пользователя в одной сессии браузера (requirements SEC-5).
 */

const SUBSCRIPTION_TTL_MS = 60 * 1000;

let cached: {data: Subscription; expiresAt: number} | null = null;
let inflight: Promise<Subscription> | null = null;
const listeners = new Set<() => void>();

/** Подписка текущего пользователя из кеша (или одним сетевым запросом). */
export async function fetchCachedSubscription(): Promise<Subscription> {
    if (cached && cached.expiresAt > Date.now()) {
        return cached.data;
    }
    if (inflight) {
        return inflight;
    }
    inflight = (async () => {
        const data = await fetchCurrentSubscription();
        cached = {data, expiresAt: Date.now() + SUBSCRIPTION_TTL_MS};
        return data;
    })();
    try {
        return await inflight;
    } finally {
        inflight = null;
    }
}

/** Уже загруженная подписка, если она ещё свежая; иначе `null` (без сетевого запроса). */
export function peekCachedSubscription(): Subscription | null {
    return cached && cached.expiresAt > Date.now() ? cached.data : null;
}

/**
 * Сброс кеша с уведомлением смонтированных хуков: зовётся после смены тарифа
 * (в том числе администратором), при выходе из аккаунта и из тестов.
 */
export function invalidateSubscriptionCache(): void {
    cached = null;
    inflight = null;
    listeners.forEach(listener => listener());
}

/** Подписка на инвалидацию; возвращает функцию отписки. */
export function subscribeToSubscriptionCache(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
